import { type JSX, useEffect, useMemo, useState } from 'react';

import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Skelett } from '../start/Widget';

import {
  type Berichtsergebnis,
  type Berichtsvorlage,
  type GespeicherterBericht,
  type Kennzahlenkatalog,
  FREIGABENAMEN,
  aufzaehlung,
  berichtAnlegen,
  berichtErgebnis,
  berichte as holeBerichte,
  vorlagen as holeVorlagen,
  katalog as holeKatalog,
  zeitpunktText,
  zeitraumText,
} from '../api/berichte';
import { ApiFehler } from '../api/fehler';
import { Kennzahlenleiste, Reiterleiste } from '../komponenten/Akte';
import { Diagramm, Kreisdiagramm } from '../komponenten/Diagramm';
import { Meldung } from '../komponenten/Meldung';
import type { Kopf } from '../komponenten/Rahmen';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { bereiteKennzahlen } from './auswahl';
import { Zielwerte } from './Zielwerte';
import { achsenformat, kreisAus, kreisUntauglich, punkteAus, wirksameDarstellung } from './Ergebnis';

/**
 * DIE BERICHTSLISTE (AUS-16, Auftrag #24) — Seitentyp Übersicht.
 *
 * Die eigenen Berichte als Kacheln: ein kleines Diagramm, Titel, Kennzahl,
 * Gruppierung, Zeitraum und „geändert". Ein Klick öffnet den Bericht im
 * Baukasten.
 *
 * DAS KLEINE DIAGRAMM RECHNET WIRKLICH. Jede Kachel rechnet ihren Bericht
 * mit den Rechten des BETRACHTERS von JETZT (BEN-05, AUS-06) — auch einen
 * geteilten. Fehlt ein Fachrecht, sagt die Kachel das, statt ein Bild zu
 * zeigen, das nicht stimmt.
 *
 * „GETEILT MIT MIR" (Auftrag #27) steht als Reiter da, sobald es solche
 * Berichte gibt — vorher wäre er eine Ankündigung ohne Inhalt.
 *
 * „VORLAGEN" (Auftrag #28): fertige Berichte aus dem Code, die jeder als
 * eigenen übernimmt (AUS-02, AUS-03, AUS-04, AUS-21). Was sich nicht sauber
 * rechnen lässt (Region, Mahnquote), steht mit seinem Grund da — ohne Knopf.
 */
const VERGLEICHSKURZ = {
  vorjahr: 'Vorjahr',
  vor_zwei_jahren: 'vor zwei Jahren',
  vorperiode: 'Vorperiode',
  frei: 'freiem Vergleich',
} as const;

export function Berichtsliste({
  beiOeffnen,
  beiKopf,
  darfZielePflegen = false,
}: {
  readonly beiOeffnen: (id: string) => void;
  readonly beiKopf: (kopf: Kopf) => void;
  /** Auftrag #29: `auswertung:zielwerte_pflegen` (AeA-43). */
  readonly darfZielePflegen?: boolean;
}): JSX.Element {
  const [reiter, setzeReiter] = useState<'meine' | 'geteilt' | 'vorlagen' | 'zielwerte'>('meine');
  /* Auftrag CRM-165: drei Stufen statt „wird geladen"; „Erneut versuchen" lädt nur diese Seite. */
  const geladen = useLadestufe((signal) =>
    Promise.all([holeBerichte(signal), holeKatalog(signal), holeVorlagen(signal)]),
  );
  const [liste, katalog, vorlagen] = geladen.daten ?? [undefined, undefined, [] as readonly Berichtsvorlage[]];
  const fehler = geladen.fehler instanceof ApiFehler ? geladen.fehler : undefined;

  /* Stabil je Liste — sie stehen in den Abhaengigkeiten des Seitenkopfs. */
  const meine = useMemo(() => liste?.filter((b) => b.eigen), [liste]);
  const geteilt = useMemo(() => liste?.filter((b) => !b.eigen) ?? [], [liste]);
  useZaehler(zaehlwort(meine?.length, 'Bericht', 'Berichte'));

  const zuletzt = meine?.[0]?.geaendertAm;
  useEffect(() => {
    beiKopf({
      titel: 'Berichte',
      unterzeile: 'Eigene Auswertungen aus dem Kennzahlenkatalog — bauen, speichern, als CSV ausgeben',
      sinnbild: 'chart-bar:light',
      kennzeichen: undefined,
      kennzahlen: (
        <Kennzahlenleiste
          zahlen={[
            { beschriftung: 'Meine Berichte', wert: meine === undefined ? '…' : String(meine.length) },
            {
              beschriftung: 'Kennzahlen im Katalog',
              // Auftrag CRM-44: gezaehlt wird, was rechnet; die vorbereiteten stehen in der Suche des Baukastens.
              wert: katalog === undefined ? '…' : String(bereiteKennzahlen(katalog.kennzahlen).length),
            },
            {
              beschriftung: 'Zuletzt geändert',
              wert: zuletzt === undefined ? '—' : zeitpunktText(zuletzt),
            },
          ]}
        />
      ),
    });
  }, [beiKopf, liste, meine, katalog, zuletzt]);

  if (liste === undefined || meine === undefined || katalog === undefined) {
    return (
      <>
        {fehler !== undefined && <Meldung fehler={fehler} />}
        <Ladeanzeige zustand={geladen} was="Die Berichte" skelett={<Berichtsskelett />} />
      </>
    );
  }

  const mitZielen = katalog !== undefined && bereiteKennzahlen(katalog.kennzahlen).length > 0;
  const hier =
    reiter === 'geteilt' && geteilt.length > 0
      ? 'geteilt'
      : reiter === 'vorlagen' && vorlagen.length > 0
        ? 'vorlagen'
        : reiter === 'zielwerte' && mitZielen
          ? 'zielwerte'
          : 'meine';
  const gezeigt = hier === 'geteilt' ? geteilt : meine;
  const reiterliste = [
    { kennung: 'meine' as const, beschriftung: 'Meine Berichte', zahl: meine.length },
    ...(geteilt.length > 0 ? [{ kennung: 'geteilt' as const, beschriftung: 'Geteilt mit mir', zahl: geteilt.length }] : []),
    ...(vorlagen.length > 0 ? [{ kennung: 'vorlagen' as const, beschriftung: 'Vorlagen', zahl: vorlagen.length }] : []),
    /* Auftrag #29 (AUS-12): Zielwerte ansehen und, mit dem Recht, pflegen. */
    ...(mitZielen ? [{ kennung: 'zielwerte' as const, beschriftung: 'Zielwerte' }] : []),
  ];

  return (
    <section className="karte" aria-labelledby="meine-berichte">
      <div className="karte__titel">
        <h2 id="meine-berichte">
          {hier === 'geteilt'
            ? 'Geteilt mit mir'
            : hier === 'vorlagen'
              ? 'Vorlagen'
              : hier === 'zielwerte'
                ? 'Zielwerte'
                : 'Meine Berichte'}
        </h2>
        <small>zuletzt geändert zuerst</small>
      </div>
      {reiterliste.length > 1 && (
        <Reiterleiste beschriftung="Berichte" praefix="berichte-" gewaehlt={hier} beiWahl={setzeReiter} reiter={reiterliste} />
      )}
      <div
        id={`tafel-berichte-${hier}`}
        role={reiterliste.length > 1 ? 'tabpanel' : undefined}
        aria-labelledby={reiterliste.length > 1 ? `reiter-berichte-${hier}` : undefined}
      >
        {hier === 'zielwerte' ? (
          <Zielwerte katalog={katalog} darfPflegen={darfZielePflegen} />
        ) : hier === 'vorlagen' ? (
          <Vorlagenliste vorlagen={vorlagen} beiUebernommen={beiOeffnen} />
        ) : gezeigt.length === 0 ? (
          <p className="hilfe">
            Noch kein Bericht gespeichert. „Neuer Bericht" öffnet den Baukasten: Kennzahl wählen,
            gruppieren, filtern — und speichern, was Sie öfter brauchen.
          </p>
        ) : (
          <ul className="berichtskacheln">
            {gezeigt.map((b) => (
              <li key={b.id}>
                <Berichtskachel bericht={b} katalog={katalog} beiOeffnen={() => beiOeffnen(b.id)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Berichtskachel({
  bericht,
  katalog,
  beiOeffnen,
}: {
  readonly bericht: GespeicherterBericht;
  readonly katalog: Kennzahlenkatalog;
  readonly beiOeffnen: () => void;
}): JSX.Element {
  const b = bericht.beschreibung;
  const gruppe = katalog.gruppierungen.find((g) => g.kennung === b.gruppierung);
  /*
   * Mit den Rechten des Betrachters — auch fuer geteilte Berichte (Auftrag #27).
   * Die Beschreibung ist Teil des Berichts; sie aendert sich nur mit ihm.
   * Auftrag CRM-165: jede Kachel laedt fuer sich, mit drei Stufen.
   */
  const geladen = useLadestufe((signal) => berichtErgebnis(bericht.id, signal), `${bericht.id}|${bericht.geaendertAm}`);
  const antwort: Berichtsergebnis | 'fehlt' | undefined =
    geladen.stufe === 'fertig' ? geladen.daten : geladen.stufe === 'gescheitert' && geladen.fehler !== undefined ? 'fehlt' : undefined;

  const ergebnis = antwort === 'fehlt' ? 'fehlt' : antwort === undefined ? undefined : (antwort.ergebnis ?? 'ohneRecht');

  /* Die Namen auch der Kennzahlen, deren Recht fehlt — die API nennt sie (Auftrag #27). */
  const namen = b.kennzahlen.map(
    (kennung) =>
      (typeof antwort === 'object'
        ? (antwort.ergebnis?.kennzahlen.find((k) => k.kennung === kennung)?.name
          ?? antwort.fehlend.find((k) => k.kennung === kennung)?.name)
        : undefined)
      ?? katalog.kennzahlen.find((k) => k.kennung === kennung)?.name
      ?? 'Kennzahl nicht verfügbar',
  );
  const was = [
    aufzaehlung(namen),
    gruppe === undefined ? null : `je ${gruppe.name}`,
    zeitraumText(b.zeitraum),
    /* Auftrag #26: die Vergleiche in Kurzform. */
    b.vergleiche.length === 0 ? null : `mit ${b.vergleiche.map((v) => VERGLEICHSKURZ[v.art]).join(' und ')}`,
  ]
    .filter((t) => t !== null)
    .join(' · ');

  return (
    <>
    <button type="button" className="berichtskachel" onClick={beiOeffnen}>
      {ergebnis === undefined ? (
        /* Skelett, nach drei Sekunden der Hinweis; die Absage steht unter der Kachel (kein Knopf im Knopf). */
        geladen.stufe === 'gescheitert' ? (
          <span className="berichtskachel__leer">Konnte nicht geladen werden.</span>
        ) : (
          <Ladeanzeige zustand={geladen} was="Das Ergebnis" skelett={<Skelett art="verlauf" />} />
        )
      ) : ergebnis === 'fehlt' || ergebnis === 'ohneRecht' ? (
        <span className="berichtskachel__leer">
          {ergebnis === 'ohneRecht'
            ? 'Für diese Kennzahl fehlt Ihnen das Recht.'
            : 'Das Ergebnis konnte nicht geladen werden.'}
        </span>
      ) : b.darstellung === 'kreis' && kreisUntauglich(ergebnis) === null ? (
        <Kreisdiagramm klein stuecke={kreisAus(ergebnis)} beschriftung={bericht.titel} />
      ) : (
        <Diagramm
          klein
          art={wirksameDarstellung(b.darstellung, ergebnis) === 'linie' ? 'linie' : 'balken'}
          punkte={
            ergebnis.zeilen.length > 0
              ? punkteAus(ergebnis)
              : [
                  {
                    schluessel: 'gesamt',
                    beschriftung: 'Gesamt',
                    wert: ergebnis.gesamt.werte[0]?.wert == null ? null : Number(ergebnis.gesamt.werte[0].wert),
                    unvollstaendig: ergebnis.gesamt.unvollstaendig,
                    wertText: '',
                  },
                ]
          }
          beschriftung={bericht.titel}
          achse={achsenformat(ergebnis.kennzahlen[0]!.einheit)}
        />
      )}
      <span className="berichtskachel__name">{bericht.titel}</span>
      <span className="berichtskachel__was">{was}</span>
      <span className="berichtskachel__fuss">
        <span className="marke">{FREIGABENAMEN[bericht.freigabe]}</span>
        {!bericht.eigen && <span>von {bericht.eigentuemer}</span>}
        <span>geändert {zeitpunktText(bericht.geaendertAm)}</span>
      </span>
    </button>
    {geladen.stufe === 'gescheitert' && (
      <button className="knopf knopf--klein berichtskachel__erneut" type="button" onClick={geladen.erneut}>
        Erneut versuchen
      </button>
    )}
    </>
  );
}

/** Stufe 1 der Berichtsliste: Kopf und Kacheln in der Form, die gleich kommt. */
function Berichtsskelett(): JSX.Element {
  return (
    <section className="karte">
      <Skelett art="liste" />
      <ul className="berichtskacheln">
        {[0, 1, 2, 3].map((i) => (
          <li key={i}>
            <Skelett art="karte" />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * DIE VORLAGEN (Auftrag #28). „Übernehmen" legt eine private Kopie als
 * eigenen Bericht an und öffnet sie. Ohne Fachrecht für eine Kennzahl der
 * Vorlage sagt die Karte es; eine Vorlage ohne Beschreibung sagt, warum sie
 * nicht rechnet.
 */
function Vorlagenliste({
  vorlagen,
  beiUebernommen,
}: {
  readonly vorlagen: readonly Berichtsvorlage[];
  readonly beiUebernommen: (id: string) => void;
}): JSX.Element {
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState<string | undefined>(undefined);

  async function uebernehmen(v: Berichtsvorlage): Promise<void> {
    if (v.beschreibung === null) return;
    setzeLaeuft(v.kennung);
    try {
      const neu = await berichtAnlegen({ titel: v.titel, beschreibung: v.beschreibung });
      beiUebernommen(neu.id);
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    } finally {
      setzeLaeuft(undefined);
    }
  }

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      <ul className="vorlagen">
        {vorlagen.map((v) => (
          <li key={v.kennung} className="vorlage">
            <span className="vorlage__kopf">
              <span className="berichtskachel__name">{v.titel}</span>
            </span>
            <span className="berichtskachel__was">{v.erklaerung}</span>
            {v.nichtRechenbar !== null ? (
              <span className="hinweiszeile">Nicht gerechnet: {v.nichtRechenbar}</span>
            ) : v.fehlend.length > 0 ? (
              <span className="hinweiszeile">
                Ihnen fehlt das Recht für {v.fehlend.map((f) => `„${f.name}"`).join(' und ')}.
              </span>
            ) : (
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={laeuft !== undefined}
                onClick={() => void uebernehmen(v)}
              >
                Als eigenen Bericht übernehmen
              </button>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
